import PropTypes from 'prop-types';

import React, {useEffect, useState} from 'react';
import {Loader} from 'components';

import DebouncedInput from 'components/TabularDataBasic/DebouncedInput';
import Header from 'components/TabularDataBasic/Header';
import ColumnFooter from 'components/TabularDataBasic/ColumnFooter';
import Paginator from 'components/TabularDataBasic/Paginator';
import TableFooter from 'components/TabularDataBasic/TableFooter';
import {recurseAllObjectsByKey} from 'libs/datatable';

import '../../styles/datatable.scss';

import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
  flexRender,
} from '@tanstack/react-table';
import {fuzzyFilter} from 'libs/datatable';
import {ListGroup, ListGroupItem} from 'react-bootstrap';

const DataTableSpreadsheet = ({
  data,
  isFetching,
  defaultColumns,
  filterAllColumns,
  showColumnFooters,
  showPagination,
  noTableFooter,
  optionalFooterBody,
  enableColumnResizing,
  enableColumnPinning,
  tableHeight,
  tableWidth,
  maxHeight,
  getCellClass,
  getGroupBorderStyle,
  collapseData,
  initialCallback,
  enableContextMenu,
  contextMenu,
  ...rest
}) => {
  const [columns, setColumns] = useState([...defaultColumns]);
  const [expanded, setExpanded] = useState({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnPinning, setColumnPinning] = useState({});
  const [sorting, setSorting] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [clicked, setClicked] = useState(false);
  const [points, setPoints] = useState({
    x: 0,
    y: 0,
  });

  const finalData = collapseData ? collapseData(data) : data;

  const styles = {
    position: 'fixed',
    zIndex: 999,
    minWidth: 200,
    top: points.y - document.getElementsByTagName('html')[0].scrollTop,
    left: points.x - document.getElementsByTagName('html')[0].scrollLeft,
  };

  useEffect(() => {
    const handleClick = () => setClicked(false);
    window.addEventListener('click', handleClick);
    window.addEventListener('scroll', handleClick);

    return () => {
      window.removeEventListener('click', handleClick);
    };
  }, []);

  const table = useReactTable({
    data: finalData,
    columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    state: {
      expanded,
      columnFilters,
      globalFilter,
      columnVisibility,
      columnOrder,
      columnPinning,
      sorting,
    },
    columnResizeMode: 'onChange',
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    onExpandedChange: setExpanded,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    globalFilterFn: fuzzyFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getSubRows: (row) => row.subRows,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : '',
    getExpandedRowModel: getExpandedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    enablePinning: true,
    enableColumnResizing,
    enableColumnPinning,
    ...rest,
  });

  useEffect(() => {
    if (initialCallback) {
      initialCallback(table);
    }
  }, []);

  useEffect(() => {
    setColumnPinning({left: recurseAllObjectsByKey(columns, 'columns', 'isLeftPinned'), right: []});
  }, [
    JSON.stringify(columns.filter((column) =>
      column.isLeftPinned || Array.isArray(column.columns)).map((column) =>
      column.id ? column.id : column.accessorKey,
    )),
  ]);

  // logic to pin a specific column
  // 1. modify the isPinned property
  // 2. sort the columns so that isPinned is first
  // 3. use setColumns to re-render the component
  const onPinColumn = (id, isPinned = false) => {
    const newCols = columns.map((col) => {
      if (col.id === id) {
        return {
          ...col,
          isPinned,
        };
      }
      return col;
    });

    newCols.sort((a, b) => {
      const aPinned = a.isPinned ? 1 : 0;
      const bPinned = b.isPinned ? 1 : 0;
      return bPinned - aPinned;
    });

    return setColumns([...newCols]);
  };

  const classNames = (params, extraClass) => {
    let classes = '';
    Object.keys(params).forEach((key) => {
      if (params[key] === true) {
        classes = `${classes}${key} `;
      }
    });
    if (extraClass) {
      classes = `${classes} ${extraClass}`;
    }
    return classes;
  };

  const headCells = table.getHeaderGroups().map((headerGroup) => {
    let pinLeftWidth = 0;
    let headerGroupCells = [];

    headerGroup.headers.forEach((header) => {
      const colClassName = classNames({
        'p-2 text-left': true,
        'bg-light-gray': !header.column.getIsPinned(),
        'sticky-col bg-dark-gray text-medium-gray': !!header.column.getIsPinned(),
      });

      if (header.column.getIsPinned()) {
        headerGroupCells.push(<Header
          key={header.id}
          properties={header}
          table={table}
          columnResizeMode={'onChange'}
          pinLeftWidth={pinLeftWidth ? pinLeftWidth : 0}
          colClassName={colClassName}
          customPinning={false}
          pinningHandler={onPinColumn}
          style={header.column.columnDef.headerStyle}
        />);

        pinLeftWidth += header.column.getSize();
      } else {
        headerGroupCells.push(<Header
          key={header.id}
          properties={header}
          table={table}
          columnResizeMode={'onChange'}
          colClassName={colClassName}
          customPinning={false}
          pinningHandler={onPinColumn}
          style={header.column.columnDef.headerStyle}
        />);
      }

    });
    return <tr key={headerGroup.id}>
      {headerGroupCells}
    </tr>;
  });

  const rowCells = table.getRowModel().rows.map((row) => {
    let pinLeftWidth = 0;
    let cells = [];
    let cellClass = getCellClass ? getCellClass(row) : '';

    row.getVisibleCells().forEach((cell) => {
      let borderStyle = getGroupBorderStyle ? getGroupBorderStyle(cell) : '';

      let contextMenuCallback = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setClicked(true);
        setSelectedRow(row);
        setSelectedColumn(cell);
        setPoints({
          x: e.pageX,
          y: e.pageY,
        });
      };

      if (cell.column.getIsPinned()) {
        cells.push(<td
          className={classNames({
            'p-2': true,
            'sticky-col bg-light-gray': !!cell.column.getIsPinned(),
          }, cellClass)}
          style={{
            width: isNaN(cell.column.getSize()) ? 0 : cell.column.getSize(),
            ...cell.column.columnDef.cellStyle,
            left: pinLeftWidth ? pinLeftWidth : '0',
            ...borderStyle,
          }}
          key={cell.id}
          {...((enableContextMenu && cell.column.columnDef.enableContextMenu) ? {onContextMenu: contextMenuCallback} : {})}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>);

        pinLeftWidth += cell.column.getSize();
      } else {
        cells.push(<td
          className={`p-2 ${cellClass}`}
          style={{
            width: isNaN(cell.column.getSize()) ? 0 : cell.column.getSize(),
            ...cell.column.columnDef.cellStyle,
            ...borderStyle,
          }}
          key={cell.id}
          {...((enableContextMenu && cell.column.columnDef.enableContextMenu) ? {onContextMenu: contextMenuCallback} : {})}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>);
      }
    });

    return (
      <tr key={row.id} className="text-left">
        {cells}
      </tr>
    );
  });

  const tableContainerStyle = {};
  const tableStyle = {};
  if (maxHeight) {
    tableContainerStyle.maxHeight = maxHeight;
  }
  if (tableHeight) {
    tableStyle.height = tableHeight;
  }
  tableStyle.width = tableWidth ? tableWidth : table.getTotalSize();

  return (
    <div className='TabularDataBasic DataTableSpreadsheet'>
      <div className='p-2'>
        {filterAllColumns &&
          <div className={'filter-container pull-right'}>
            <label style={{paddingRight: '1em'}}>Search</label>
            <DebouncedInput
              value={globalFilter ? globalFilter : ''}
              onChange={(value) => setGlobalFilter(String(value))}
              className='p-2 font-lg shadow border border-block sticky-col'
              placeholder='Search all columns...'
            />
          </div>
        }
        <div className='h-2' />
        <div className='overflow-auto w-full'
          style={tableContainerStyle}
        >
          <table className='table table-striped table-fixed w-full'
            style={tableStyle}
          >
            <thead>
              {headCells}
            </thead>
            <tbody>
              {rowCells}
            </tbody>
            {showColumnFooters &&
              <ColumnFooter
                footerGroups={table.getFooterGroups()}
              />
            }
          </table>
          {enableContextMenu && clicked && (
            <div style={styles}>
              <ListGroup>
                {contextMenu.map(({id, label, onSelect, renderOption}, idx) => (
                  <ListGroupItem className={'no-margin'} key={idx} id={id}
                    onClick={() => onSelect ? onSelect(label, selectedRow, selectedColumn) : null}>
                    {renderOption ? renderOption(label, selectedRow, selectedColumn) : label}
                  </ListGroupItem>
                ))}
              </ListGroup>
            </div>
          )}
        </div>
      </div>
      <div>
        {showPagination &&
          <Paginator
            setPageIndex={table.setPageIndex}
            getCanPreviousPage={table.getCanPreviousPage}
            getCanNextPage={table.getCanNextPage}
            previousPage={table.previousPage}
            nextPage={table.nextPage}
            getPageCount={table.getPageCount}
            setPageSize={table.setPageSize}
            currentPageIndex={table.getState().pagination.pageIndex}
            currentPageSize={table.getState().pagination.pageSize}
            rowCount={table.getPrePaginationRowModel().rows.length}
          />
        }
        {(optionalFooterBody || (!showPagination && !noTableFooter)) &&
          <TableFooter
            optionalFooterBody={optionalFooterBody}
            rowCount={table.getPrePaginationRowModel().rows.length}
            totalCount={table.getRowModel().rows.length}
          />
        }
      </div>

      {isFetching &&
        <div className='loading-overlay'>
          <Loader />
        </div>
      }
    </div>
  );
};

DataTableSpreadsheet.propTypes = {
  data: PropTypes.array.isRequired,
  defaultColumns: PropTypes.array.isRequired,
  isFetching: PropTypes.bool,
  filterAllColumns: PropTypes.bool,
  showColumnFooters: PropTypes.bool,
  showPagination: PropTypes.bool,
  noTableFooter: PropTypes.bool,
  enableColumnResizing: PropTypes.bool,
  enableColumnPinning: PropTypes.bool,
  optionalFooterBody: PropTypes.element,
  tableHeight: PropTypes.string,
  tableWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  getCellClass: PropTypes.func,
  getGroupBorderStyle: PropTypes.func,
  collapseData: PropTypes.func,
  initialCallback: PropTypes.func,
  enableContextMenu: PropTypes.bool,
  contextMenu: PropTypes.array,
};

DataTableSpreadsheet.defaultProps = {
  isFetching: false,
  filterAllColumns: true,
  showColumnFooters: false,
  showPagination: true,
  noTableFooter: false,
  enableColumnResizing: true,
  enableColumnPinning: true,
  tableHeight: '100%',
  tableWidth: '100%',
  maxHeight: '100%',
  enableContextMenu: false,
};

export default DataTableSpreadsheet;
